import React, { useState } from "react";
import * as arrow from "apache-arrow";
import { Box } from "@mui/material";
import { getFlatTableData } from "./mui_flat_table_data";
import { MUIFlatTableColumn } from "../components/universal_mui_table/MUIFlatTable";
import CompactTableTextField from "../components/transformed_custom_view/viewer/CompactTableTextField";

// Format date for display (MM/DD/YY) in UTC timezone
const formatDateForDisplay = (dateValue: string | Date) => {
  if (!dateValue) return "";

  // If it's already in MM/DD/YY format, return as is
  if (
    typeof dateValue === "string" &&
    /^\d{2}\/\d{2}\/\d{2}$/.test(dateValue)
  ) {
    return dateValue;
  }
  // If it's already in MM/DD/YYYY format, convert to MM/DD/YY
  if (
    typeof dateValue === "string" &&
    /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)
  ) {
    const [mm, dd, yyyy] = dateValue.split("/");
    return `${mm}/${dd}/${yyyy.slice(-2)}`;
  }

  // Handle Date objects directly - format in UTC timezone
  if (dateValue instanceof Date) {
    if (!isNaN(dateValue.getTime())) {
      const month = String(dateValue.getUTCMonth() + 1).padStart(2, "0");
      const day = String(dateValue.getUTCDate()).padStart(2, "0");
      const year = String(dateValue.getUTCFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    }
    // If it's an invalid Date, try to handle it gracefully
    console.warn("Invalid Date object received:", dateValue);
    return "";
  }

  // Try to parse string dates and convert to MM/DD/YY in UTC
  if (typeof dateValue === "string") {
    // Check if it's a Unix timestamp in milliseconds (13 digits)
    if (/^\d{13}$/.test(dateValue)) {
      const date = new Date(parseInt(dateValue, 10));
      if (!isNaN(date.getTime())) {
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        const year = String(date.getUTCFullYear()).slice(-2);
        return `${month}/${day}/${year}`;
      }
    }

    // Try regular date parsing
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const year = String(date.getUTCFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    }
    // If it's an invalid date string, try to handle it gracefully
    console.warn("Invalid date string received:", dateValue);
  }

  // If we get here, the value couldn't be parsed as a date
  console.warn("Could not format date value:", dateValue, typeof dateValue);
  return String(dateValue);
};

// Component that manages its own state for each input field
const EditableCell = ({
  value,
  onChange,
  isQuantity,
  isRate,
  isDate = false,
}: {
  value: string | Date;
  onChange: (value: string) => void;
  isQuantity: boolean;
  isRate: boolean;
  isDate?: boolean;
}) => {
  const [localValue, setLocalValue] = useState(
    isDate && value instanceof Date
      ? formatDateForDisplay(value)
      : String(value)
  );
  const [hasUserEdited, setHasUserEdited] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    setHasUserEdited(true);
    onChange(newValue);
  };

  return (
    <Box
      sx={{
        p: 1,
        height: "40px",
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <CompactTableTextField
        value={
          isDate && !hasUserEdited
            ? formatDateForDisplay(localValue)
            : localValue
        }
        onChange={handleChange}
        placeholder={isDate ? "MM/DD/YY" : ""}
        sx={{ width: "100%" }}
      />
    </Box>
  );
};

export function getPurchaseOrderExportTableData({
  arrowTable,
  columns,
  viewParams,
  purchaseOrderConfig,
  sortConfig = [],
  onSort,
  columnSchema,
  setSortConfig,
  theme,
  expandedGroups,
  setExpandedGroups,
  columnHeaderMap,
  firstMount,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: any;
  purchaseOrderConfig?: {
    getInputs: (field: string) => Record<string, string>;
    setInputs: (
      field: string,
      updater: React.SetStateAction<Record<string, string>>
    ) => void;
    editableFields: string[];
  };
  sortConfig?: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  setSortConfig?: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  theme?: any;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  columnHeaderMap?: Record<string, string>;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  // Get the base flat table data
  const baseTableData = getFlatTableData({
    arrowTable,
    columns,
    sortConfig: sortConfig.map(({ key, direction }) => ({ key, direction })),
    onSort,
    columnSchema,
    setSortConfig,
    firstMount,
  });

  if (!baseTableData || !purchaseOrderConfig) {
    return baseTableData;
  }

  const { getInputs, setInputs } = purchaseOrderConfig;

  // Define the actual column keys that are editable (these match the table columns)
  const editableColumnKeys = [
    "quantity",
    "rate",
    "item rate",
    "PO",
    "PO deliver",
    "shipment method",
    "memo",
    "Override PO Print Slip Name",
  ];

  // Create a unique key for each row based on all non-editable columns
  const getRowKey = (row: Record<string, any>): string => {
    const keyFields = columns
      .map((col) => col.name)
      .filter((name) => !editableColumnKeys.includes(name));
    return keyFields.map((field) => String(row[field] || "")).join("||");
  };

  // Override the columns to make specified columns editable
  const editableColumns: MUIFlatTableColumn[] = baseTableData.columns.map(
    (column) => {
      if (editableColumnKeys.includes(column.key)) {
        return {
          ...column,
          renderCell: (row: Record<string, any>, rowIndex: number) => {
            const rowKey = getRowKey(row);
            const columnKey = column.key;

            // Map column keys to config field names
            const getFieldName = (key: string): string => {
              const fieldMap: Record<string, string> = {
                quantity: "quantity",
                rate: "rate",
                "item rate": "rate",
                PO: "po",
                "PO deliver": "po_deliver",
                "shipment method": "shipment_method",
                memo: "memo",
                "Override PO Print Slip Name": "override_po_print_slip_name",
              };
              return fieldMap[key] || key;
            };

            const fieldName = getFieldName(columnKey);
            const fieldInputs = getInputs(fieldName);
            let initialValue = String(row[columnKey] || "");

            // Special handling for date fields
            if (columnKey === "PO deliver") {
              const rawValue = row[columnKey];
              const formattedValue =
                fieldInputs[rowKey] ?? formatDateForDisplay(rawValue);
              initialValue = formattedValue;
            } else {
              initialValue = fieldInputs[rowKey] ?? initialValue;
            }

            const handleChange = (value: string) => {
              setInputs(fieldName, (prev) => ({ ...prev, [rowKey]: value }));
            };

            return (
              <EditableCell
                value={initialValue}
                onChange={handleChange}
                isQuantity={columnKey === "quantity"}
                isRate={columnKey === "rate" || columnKey === "item rate"}
                isDate={columnKey === "PO deliver"} // Keep date formatting for initial display
              />
            );
          },
        };
      }
      return column;
    }
  );

  // Override the getRow function to return edited values
  const getRowWithEdits = (rowIndex: number): Record<string, any> => {
    const originalRow = baseTableData.getRow(rowIndex);
    const rowKey = getRowKey(originalRow);
    const updatedRow = { ...originalRow };

    // Update all editable fields if edited
    const fieldMappings: Record<string, string[]> = {
      quantity: ["quantity"],
      rate: ["rate", "item rate"],
      po: ["PO"],
      po_deliver: ["PO deliver"],
      shipment_method: ["shipment method"],
      memo: ["memo"],
      override_po_print_slip_name: ["Override PO Print Slip Name"],
    };

    Object.entries(fieldMappings).forEach(([fieldName, columnKeys]) => {
      const fieldInputs = getInputs(fieldName);
      if (fieldInputs[rowKey] !== undefined) {
        columnKeys.forEach((columnKey) => {
          if (originalRow.hasOwnProperty(columnKey)) {
            updatedRow[columnKey] = fieldInputs[rowKey];
          }
        });
      }
    });

    return updatedRow;
  };

  // Apply column header mapping if provided
  const finalColumns = editableColumns.map((column) => ({
    ...column,
    label: columnHeaderMap?.[column.key] || column.label || column.key,
  }));

  return {
    ...baseTableData,
    columns: finalColumns,
    getRow: getRowWithEdits,
  };
}
